import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getCategoryIcon } from './CategoryIcons';
import { recordCategoryInteraction } from '../../utils/userPreferences';

/**
 * AllCategoriesModal
 * Clean, compact category selector modal with SVG icons, live search, and subcategory explorer.
 */
function AllCategoriesModal({ isOpen, onClose, categories = [] }) {
    const history = useHistory();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryDetails, setCategoryDetails] = useState({});
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);

    // Reset state on open/close
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setSelectedCategory(null);
        }
    }, [isOpen]);

    // Handle ESC key to close
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Fetch subcategories when a category is selected if not already cached
    const handleCategoryClick = async (cat) => {
        recordCategoryInteraction(cat.name || cat.id);

        const catId = cat._id || cat.id;
        if (!categoryDetails[catId]) {
            setIsLoadingDetails(true);
            try {
                const res = await axios.get(`/api/categories/${catId}`);
                if (res.data && res.data.category) {
                    setCategoryDetails(prev => ({
                        ...prev,
                        [catId]: res.data.category
                    }));
                }
            } catch (err) {
                console.error("Failed to load subcategories for category:", err);
            } finally {
                setIsLoadingDetails(false);
            }
        }
        setSelectedCategory(cat);
    };

    const handleNavigate = (categoryName, subcategoryName = '') => {
        recordCategoryInteraction(categoryName);
        if (subcategoryName) recordCategoryInteraction(subcategoryName);
        onClose();

        let url = `/marketplace/search?category=${encodeURIComponent(categoryName)}`;
        if (subcategoryName) {
            url += `&subcategory=${encodeURIComponent(subcategoryName)}`;
        }
        history.push(url);
    };

    // Filter categories based on search query
    const filteredCategories = useMemo(() => {
        if (!searchQuery.trim()) return categories;
        const q = searchQuery.toLowerCase().trim();
        return categories.filter(c => {
            const nameMatch = c.name?.toLowerCase().includes(q);
            const descMatch = c.desc?.toLowerCase().includes(q) || c.tagline?.toLowerCase().includes(q);
            return nameMatch || descMatch;
        });
    }, [categories, searchQuery]);

    if (!isOpen) return null;

    const currentDetails = selectedCategory ? (categoryDetails[selectedCategory._id || selectedCategory.id] || selectedCategory) : null;
    const subcategories = currentDetails?.children || currentDetails?.subcategories || [];

    return (
        <div 
            className="im-cat-modal-overlay" 
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="all-categories-title"
        >
            <div 
                className="im-cat-modal" 
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="im-cat-modal__header">
                    <div className="im-cat-modal__title-row">
                        {selectedCategory ? (
                            <button 
                                className="im-cat-modal__back-btn" 
                                onClick={() => setSelectedCategory(null)}
                                aria-label="Back to all categories"
                            >
                                <ArrowBackIcon fontSize="small" />
                                <span>All Categories</span>
                            </button>
                        ) : (
                            <h2 id="all-categories-title" className="im-cat-modal__title">
                                All Categories
                                <span className="im-cat-modal__badge">{categories.length}</span>
                            </h2>
                        )}
                        <button 
                            className="im-cat-modal__close-btn" 
                            onClick={onClose}
                            aria-label="Close categories dialog"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Search Bar */}
                    {!selectedCategory && (
                        <div className="im-cat-modal__search">
                            <SearchIcon className="im-cat-modal__search-icon" />
                            <input
                                type="text"
                                className="im-cat-modal__search-input"
                                placeholder="Search categories, subcategories..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                            />
                            {searchQuery && (
                                <button 
                                    className="im-cat-modal__search-clear" 
                                    onClick={() => setSearchQuery('')}
                                    aria-label="Clear search"
                                >
                                    <CloseIcon fontSize="small" />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Modal Body */}
                <div className="im-cat-modal__body">
                    {selectedCategory ? (
                        /* Subcategory Drilldown View */
                        <div className="im-cat-sub-view">
                            <div className="im-cat-sub-view__hero">
                                <div className="im-cat-sub-view__icon">
                                    {getCategoryIcon(selectedCategory, 28)}
                                </div>
                                <div className="im-cat-sub-view__meta">
                                    <h3>{selectedCategory.name}</h3>
                                    <p>{selectedCategory.desc || selectedCategory.tagline || 'Explore verified local shops and products.'}</p>
                                </div>
                                <button 
                                    className="im-cat-sub-view__view-all-btn"
                                    onClick={() => handleNavigate(selectedCategory.name)}
                                >
                                    Browse All {selectedCategory.name.split('&')[0]} &rarr;
                                </button>
                            </div>

                            <div className="im-cat-sub-view__content">
                                <h4 className="im-cat-sub-view__subheading">Subcategories</h4>
                                {isLoadingDetails ? (
                                    <div className="im-cat-sub-view__loading">Loading subcategories...</div>
                                ) : subcategories.length > 0 ? (
                                    <div className="im-cat-sub-grid">
                                        {subcategories.map((sub) => (
                                            <div key={sub._id || sub.name} className="im-cat-sub-card">
                                                <button 
                                                    className="im-cat-sub-card__header"
                                                    onClick={() => handleNavigate(selectedCategory.name, sub.name)}
                                                >
                                                    <span className="im-cat-sub-card__name">{sub.name}</span>
                                                    <ChevronRightIcon fontSize="small" />
                                                </button>
                                                {sub.children && sub.children.length > 0 && (
                                                    <div className="im-cat-sub-card__types">
                                                        {sub.children.slice(0, 5).map(pt => (
                                                            <button 
                                                                key={pt._id || pt.name}
                                                                className="im-cat-type-pill"
                                                                onClick={() => handleNavigate(selectedCategory.name, pt.name)}
                                                            >
                                                                {pt.name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="im-cat-sub-view__empty">
                                        <p>No specific subcategories. Explore all items in this category directly.</p>
                                        <button 
                                            className="im-cat-btn im-cat-btn--primary"
                                            onClick={() => handleNavigate(selectedCategory.name)}
                                        >
                                            View Products in {selectedCategory.name}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Compact Category Buttons Grid */
                        <>
                            {filteredCategories.length === 0 ? (
                                <div className="im-cat-modal__empty">
                                    <p>No categories found matching "{searchQuery}".</p>
                                    <button 
                                        className="im-cat-modal__clear-btn" 
                                        onClick={() => setSearchQuery('')}
                                    >
                                        Show all categories
                                    </button>
                                </div>
                            ) : (
                                <div className="im-cat-btn-grid">
                                    {filteredCategories.map((cat) => {
                                        const count = Number(cat.productCount || cat.shopCount || 0);
                                        return (
                                            <button
                                                key={cat.id || cat._id || cat.name}
                                                className="im-cat-btn"
                                                onClick={() => handleCategoryClick(cat)}
                                                title={cat.name}
                                            >
                                                <span className="im-cat-btn__icon">
                                                    {getCategoryIcon(cat, 20)}
                                                </span>
                                                <span className="im-cat-btn__label">
                                                    {cat.name}
                                                </span>
                                                {count > 0 && (
                                                    <span className="im-cat-btn__count">
                                                        {count}
                                                    </span>
                                                )}
                                                <ChevronRightIcon className="im-cat-btn__arrow" fontSize="inherit" />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="im-cat-modal__footer">
                    <span className="im-cat-modal__footer-tip">
                        Select a category to filter marketplace products or find nearby specialty shops.
                    </span>
                    <button 
                        className="im-cat-modal__footer-explore"
                        onClick={() => {
                            onClose();
                            history.push('/marketplace/search');
                        }}
                    >
                        Browse All Marketplace &rarr;
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AllCategoriesModal;
